import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { apiRequest, ApiError } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { Button, DataTable } from '../../../../shared/components/index.js';
import { useBusinessNumber } from '../../../../shared/hooks/useBusinessNumber.js';

interface TrendRow {
  readonly trendAnalysisId: string;
  readonly trendCode: string;
  readonly trendName: string;
  readonly analysisType: string;
  readonly method: string;
  readonly trendDirection: string;
  readonly confidence: string;
  readonly predictedBreach: boolean;
  readonly horizonPeriods: number;
  readonly status: string;
}

const ANALYSIS_TYPES = ['TREND_PROJECTION', 'THRESHOLD_BREACH_PREDICTION', 'WORKLOAD_FORECAST', 'CAMPAIGN_FORECAST', 'BUDGET_FORECAST', 'RISK_FORECAST', 'HISTORICAL_TREND'];
const METHODS = ['LINEAR_PROJECTION', 'MOVING_AVERAGE', 'RATE_OF_CHANGE', 'SEASONAL_NAIVE', 'THRESHOLD_RULE'];
const STATUSES = ['DRAFT', 'ACTIVE', 'ARCHIVED'];
const DIRECTIONS = ['ABOVE_IS_BAD', 'BELOW_IS_BAD'];

/**
 * M37 — Trend Analysis (§28 / §12 Predictive Analytics). Register of rule-based
 * trend / forecast computations (T501) — linear projection, moving average,
 * rate-of-change, seasonal-naïve and threshold rules (no AI/ML — that is M38).
 * An analyst computes a projection from an observed data series over a horizon
 * (TREND_MANAGE); the engine returns the direction, slope, confidence and
 * whether a configured threshold breach is predicted (BR — breach prediction).
 * Filterable by analysis type and status. Tenant isolation is enforced
 * server-side (LAW 4). A 403 surfaces a clear permission message.
 */
export function TrendAnalysisScreen(): ReactNode {
  const { decimal } = useBusinessNumber();
  const { token } = useAuth();
  const { t } = useTranslation();
  const [rows, setRows] = useState<TrendRow[]>([]);
  const [total, setTotal] = useState(0);
  const [analysisType, setAnalysisType] = useState('');
  const [status, setStatus] = useState('');
  const [q, setQ] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [reload, setReload] = useState(0);

  // Compute form
  const [trendName, setTrendName] = useState('');
  const [newType, setNewType] = useState('TREND_PROJECTION');
  const [method, setMethod] = useState('LINEAR_PROJECTION');
  const [horizon, setHorizon] = useState('6');
  const [dataPoints, setDataPoints] = useState('');
  const [breachThreshold, setBreachThreshold] = useState('');
  const [breachDirection, setBreachDirection] = useState('ABOVE_IS_BAD');
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [result, setResult] = useState<TrendRow | null>(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    const params = new URLSearchParams();
    if (analysisType) params.set('analysisType', analysisType);
    if (status) params.set('status', status);
    if (q) params.set('q', q);
    const qs = params.toString();
    apiRequest<{ data: TrendRow[]; total: number }>(`/v1/bi/trends${qs ? `?${qs}` : ''}`, { token })
      .then((r) => { if (alive) { setRows(r.data); setTotal(r.total); setError(null); } })
      .catch((e) => { if (alive) setError((e as Error).message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token, analysisType, status, q, reload]);

  const submit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setActionError(null);
    setResult(null);
    if (!trendName.trim()) { setActionError(t('m37.trend.nameRequired')); return; }
    const points = dataPoints.split(',').map((s) => Number(s.trim())).filter((n) => !Number.isNaN(n));
    if (points.length < 2) { setActionError(t('m37.trend.dataPointsRequired')); return; }
    setBusy(true);
    try {
      const r = await apiRequest<{ data: TrendRow }>(`/v1/bi/trends`, {
        method: 'POST',
        body: {
          trendName: trendName.trim(),
          analysisType: newType,
          method,
          horizonPeriods: horizon.trim() ? Number(horizon) : undefined,
          dataPoints: points,
          breachThreshold: breachThreshold.trim() ? Number(breachThreshold) : undefined,
          breachDirection: breachThreshold.trim() ? breachDirection : undefined,
        },
        token,
      });
      setResult(r.data);
      setReload((n) => n + 1);
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        setActionError(t('m37.trend.notAuth'));
      } else {
        setActionError((err as Error).message);
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <main aria-label="trend-analysis">
      <h1>{t('m37.trend.title')}</h1>

      <section aria-label="compute-trend">
        <h2>{t('m37.trend.computeTitle')}</h2>
        <form aria-label="trend-form" onSubmit={(e) => void submit(e)}>
          <label>{t('m37.common.name')}<input aria-label="field-name" value={trendName} onChange={(e) => setTrendName(e.target.value)} /></label>
          <label>{t('m37.trend.analysisType')}
            <select aria-label="field-analysis-type" value={newType} onChange={(e) => setNewType(e.target.value)}>
              {ANALYSIS_TYPES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </label>
          <label>{t('m37.trend.method')}
            <select aria-label="field-method" value={method} onChange={(e) => setMethod(e.target.value)}>
              {METHODS.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </label>
          <label>{t('m37.trend.horizon')}<input aria-label="field-horizon" type="number" value={horizon} onChange={(e) => setHorizon(e.target.value)} /></label>
          <label>{t('m37.trend.dataPoints')}<input aria-label="field-datapoints" value={dataPoints} onChange={(e) => setDataPoints(e.target.value)} placeholder={t('m37.trend.dataPointsPlaceholder')} /></label>
          <label>{t('m37.trend.breachThreshold')}<input aria-label="field-breach-threshold" type="number" value={breachThreshold} onChange={(e) => setBreachThreshold(e.target.value)} /></label>
          <label>{t('m37.trend.breachDirection')}
            <select aria-label="field-breach-direction" value={breachDirection} onChange={(e) => setBreachDirection(e.target.value)}>
              {DIRECTIONS.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </label>
          <Button variant="primary" type="submit" aria-label="submit-trend" disabled={busy}>{busy ? t('m37.trend.computing') : t('m37.trend.computeBtn')}</Button>
        </form>
        {actionError && <p role="alert" aria-label="action-error">{actionError}</p>}
        {result && (
          <dl role="status" aria-label="trend-result">
            <dt>{t('m37.common.code')}</dt><dd aria-label="result-code">{result.trendCode}</dd>
            <dt>{t('m37.trend.direction')}</dt><dd aria-label="result-direction">{result.trendDirection}</dd>
            <dt>{t('m37.common.confidence')}</dt><dd aria-label="result-confidence">{decimal(result.confidence)}</dd>
            <dt>{t('m37.trend.predictedBreach')}</dt><dd aria-label="result-breach">{result.predictedBreach ? t('common.yes') : t('common.no')}</dd>
          </dl>
        )}
      </section>

      <section aria-label="trend-list">
        <h2>{t('m37.trend.listTitle')}</h2>
        <div className="filters">
          <label>{t('m37.trend.analysisType')}
            <select aria-label="filter-analysis-type" value={analysisType} onChange={(e) => setAnalysisType(e.target.value)}>
              {['', ...ANALYSIS_TYPES].map((s) => <option key={s || 'all'} value={s}>{s || t('m37.common.all')}</option>)}
            </select>
          </label>
          <label>{t('m37.common.status')}
            <select aria-label="filter-status" value={status} onChange={(e) => setStatus(e.target.value)}>
              {['', ...STATUSES].map((s) => <option key={s || 'all'} value={s}>{s || t('m37.common.all')}</option>)}
            </select>
          </label>
          <label>{t('m37.common.search')}<input aria-label="filter-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t('m37.common.searchPlaceholder')} /></label>
        </div>
        {error && <p role="alert">{error}</p>}
        {loading ? <p>{t('common.loading')}</p> : (
          <>
            <p aria-label="trend-count">{t('m37.trend.count', { count: total })}</p>
            <DataTable
              ariaLabel="trend-table"
              rows={rows}
              rowKey={(r) => r.trendAnalysisId}
              columns={[
                { key: 'trendCode', header: t('m37.common.code'), render: (r) => r.trendCode },
                { key: 'trendName', header: t('m37.common.name'), render: (r) => r.trendName },
                { key: 'analysisType', header: t('m37.trend.colAnalysis'), render: (r) => r.analysisType },
                { key: 'method', header: t('m37.trend.method'), render: (r) => r.method },
                { key: 'trendDirection', header: t('m37.trend.direction'), render: (r) => r.trendDirection },
                { key: 'confidence', header: t('m37.common.confidence'), render: (r) => decimal(r.confidence) },
                { key: 'predictedBreach', header: t('m37.trend.colBreach'), render: (r) => (r.predictedBreach ? t('common.yes') : t('common.no')) },
                { key: 'status', header: t('m37.common.status'), render: (r) => r.status },
              ]}
            />
          </>
        )}
      </section>
    </main>
  );
}
